import type { TPaletteMode } from './palette';
import { buildGradient, buildRamp, type TColorRamp } from './ramp';

/**
 * Brand token factory.
 *
 * A partner states six identity colours (plus error/gray/black neutrals);
 * `deriveBrandBases` expands those into the full per-product set, and
 * `buildBrandTokens` expands THAT into tonal ramps, page backgrounds and
 * brand gradients. Six thin config files instead of six copies of this logic.
 */

/**
 * What a partner actually authors, per mode — six identity colours, the
 * three neutrals, and nothing else. Every product colour (Home excepted) is
 * derived from these by `deriveBrandBases`, so a partner cannot state Bird or
 * Pet directly any more — only its brand and text colours.
 */
export type TBrandSeed = {
  /**
   * The partner's three core brand colours. `primary` becomes Home verbatim;
   * `secondary` and `tertiary` are also the app's generic accent colours
   * (`theme.palette.secondary` / `.tertiary`), unchanged — they only get a
   * ramp step taken off them when seeding Trail / Bird / Baby below.
   */
  brand: {
    primary: string;
    secondary: string;
    tertiary: string;
  };
  /** Destructive actions and validation. Authored directly, not derived. */
  error: string;
  /** Neutral surfaces and dividers. Authored directly, not derived. */
  gray: string;
  /** Neutral ramp anchor behind `common.black`. Authored directly. */
  black: string;
  /**
   * Text colours. Every partner states its own triple — today's values
   * happen to agree (#000 / #666 / #FF0000), but nothing shares one constant
   * across partners, so any one of them can diverge later.
   */
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
  };
  /** Hairline divider. Defaults to DEFAULT_DIVIDER. */
  divider?: string;
};

/**
 * The fully expanded set `buildBrandTokens` turns into ramps — one base
 * colour per product/accent, rather than the six a partner authors.
 *
 * Kept as its own type (rather than folding derivation into
 * `buildBrandTokens`) so the ramp-building step stays agnostic to where its
 * ten inputs came from — useful for tests, and if a future base ever does
 * need to be authored directly instead of derived.
 */
export type TBrandBases = {
  /** Warnings. */
  secondary: string;
  /** Occasional accent. */
  tertiary: string;
  error: string;

  // Product lines — see `deriveBrandBases` for how each is seeded.
  home: string;
  bird: string;
  trail: string;
  pet: string;
  baby: string;

  /** Neutral surfaces and dividers. */
  gray: string;
  /** Text ramp: 500 = base, 300 / 200 are the secondary / tertiary steps. */
  black: string;
  /**
   * Text colours, stated explicitly rather than derived, because they are a
   * design decision rather than a step on the neutral ramp.
   */
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
  };

  /** Hairline divider. Defaults to DEFAULT_DIVIDER. */
  divider?: string;
};

/**
 * Which ramp step seeds each derived product, and from which brand/text
 * colour. Kept as named constants so the mapping reads in one place instead
 * of as magic numbers inside `deriveBrandBases`.
 */
export const PRODUCT_SEED_STEPS = {
  /** Bird and Baby share a colour on purpose — both step 600 of tertiary. */
  bird: 600,
  baby: 600,
  /** Trail takes step 700 of secondary. */
  trail: 700,
  /** Pet takes step 300 of the text-tertiary colour, not a brand colour. */
  pet: 300
} as const;

/**
 * Which brand colour tints each product's page background. Same brand
 * colours `PRODUCT_SEED_STEPS` derives the products from, but the ramp
 * itself is used here — not the ramp of the derived product colour.
 *
 *   home  -> primary        (Home IS brand.primary, so this is Home's own ramp)
 *   bird  -> tertiary
 *   baby  -> tertiary       (seeded from tertiary, same as Bird)
 *   trail -> secondary
 *   pet   -> text.tertiary
 */
export const PRODUCT_SURFACE_SOURCE = {
  home: 'primary',
  bird: 'tertiary',
  baby: 'tertiary',
  trail: 'secondary',
  pet: 'textTertiary'
} as const;

/**
 * Hairline divider default — light mode only (dark mode's divider can't be a
 * flat authored colour at all; see `buildDarkPalette` in palette.ts).
 *
 * Was a flat `#E5E7EA`, which sits at ~90% of white — numerically close to
 * Material's own convention (`rgba(0,0,0,0.12)` ≈ #E0E0E0 over white) but
 * reported live as hardly visible. An alpha overlay reads a bit more
 * assertively than that on white, and — unlike a flat hex — stays correct
 * wherever the divider actually sits (a plain white card, or a product-
 * tinted panel), since it composites against whatever's really there
 * instead of assuming white.
 */
export const DEFAULT_DIVIDER = 'rgba(0, 0, 0, 0.16)';

export type TBrandTokens = {
  tonal: Record<string, TColorRamp>;
  /**
   * The ramp each product's page background (`surface` / `surfaceStrong`)
   * reads its step from — keyed by product name. These are the partner's
   * BRAND ramps (primary / tertiary / secondary / text-tertiary), not the
   * product's own derived ramp in `tonal`: Bird is ramp(tertiary)[600], so
   * Bird's page tint comes off ramp(tertiary) itself, never a second ramp
   * built on top of that derived colour. See `PRODUCT_SURFACE_SOURCE`.
   */
  surfaceRamps: Record<string, TColorRamp>;
  /** Text colours, straight from the partner's bases. */
  text: { primary: string; secondary: string; tertiary: string };
  /** Hairline divider. */
  divider: string;
  /** `linear-gradient(176deg, <400> 0%, <500> 33%, <700> 100%)` per colour. */
  gradients: Record<string, string>;
};

/**
 * Expands a partner's six-colour seed into the ten bases `buildBrandTokens`
 * turns into ramps.
 *
 *   home      = brand.primary                    (verbatim — step 500 of a
 *               ramp is just the base, so this is a rename, not a transform)
 *   secondary = brand.secondary                   (verbatim)
 *   tertiary  = brand.tertiary                    (verbatim)
 *   bird      = ramp(brand.tertiary)[600]
 *   baby      = ramp(brand.tertiary)[600]          (same colour as bird)
 *   trail     = ramp(brand.secondary)[700]
 *   pet       = ramp(text.tertiary)[300]
 *
 * `error`, `gray`, `black` and `text` pass straight through — they are not
 * derived, a partner still states each directly.
 */
export const deriveBrandBases = (seed: TBrandSeed): TBrandBases => {
  const tertiaryRamp = buildRamp(seed.brand.tertiary);
  const secondaryRamp = buildRamp(seed.brand.secondary);
  const textTertiaryRamp = buildRamp(seed.text.tertiary);

  return {
    home: seed.brand.primary,
    secondary: seed.brand.secondary,
    tertiary: seed.brand.tertiary,
    error: seed.error,
    bird: tertiaryRamp[PRODUCT_SEED_STEPS.bird],
    baby: tertiaryRamp[PRODUCT_SEED_STEPS.baby],
    trail: secondaryRamp[PRODUCT_SEED_STEPS.trail],
    pet: textTertiaryRamp[PRODUCT_SEED_STEPS.pet],
    gray: seed.gray,
    black: seed.black,
    text: seed.text,
    divider: seed.divider
  };
};

/**
 * Expands a partner's bases into its full token set.
 *
 * Accepts any CSS colour form the palettes already use (hex or rgb/rgba).
 *
 * `mode` is currently unused — kept in the signature (and threaded through
 * by every call site) for the mode-dependent tokens a future addition here
 * may need, matching how `themes/tokens/palette.ts`'s builders already
 * split by mode. Used to pick a `pageBackground` tint step, a map nothing
 * in the app actually read; removed rather than fixed when its dark-mode
 * formula turned out to produce a near-white value.
 */
export const buildBrandTokens = (
  bases: TBrandBases,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mode: TPaletteMode = 'light'
): TBrandTokens => {
  const ramps: Record<string, TColorRamp> = {
    home: buildRamp(bases.home),
    secondary: buildRamp(bases.secondary),
    tertiary: buildRamp(bases.tertiary),
    error: buildRamp(bases.error),
    bird: buildRamp(bases.bird),
    trail: buildRamp(bases.trail),
    pet: buildRamp(bases.pet),
    baby: buildRamp(bases.baby),
    gray: buildRamp(bases.gray),
    black: buildRamp(bases.black)
  };

  // Text is deliberately excluded: a gradient on body copy fails contrast.
  const gradients = Object.fromEntries(
    Object.entries(ramps)
      .filter(([name]) => name !== 'gray' && name !== 'black')
      .map(([name, ramp]) => [name, buildGradient(ramp)])
  );

  const brandRamps: Record<
    (typeof PRODUCT_SURFACE_SOURCE)[keyof typeof PRODUCT_SURFACE_SOURCE],
    TColorRamp
  > = {
    primary: ramps.home,
    secondary: ramps.secondary,
    tertiary: ramps.tertiary,
    textTertiary: buildRamp(bases.text.tertiary)
  };
  const surfaceRamps = Object.fromEntries(
    Object.entries(PRODUCT_SURFACE_SOURCE).map(([product, source]) => [
      product,
      brandRamps[source]
    ])
  );

  return {
    tonal: ramps,
    surfaceRamps,
    text: bases.text,
    divider: bases.divider ?? DEFAULT_DIVIDER,
    gradients
  };
};
